import { useContext, useCallback } from 'react';
import { Alert, Modal, Box, Typography, Button, Link } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Upload } from 'src/components/Upload';
import AuthContext from 'src/store/auth-context';
import FileUpload from 'src/components/modal/UploadModal/FileUpload';
import AssetTableContext from 'src/store/asset-table-context';
import { useAPI } from 'src/services/api';
import { colors } from 'src/styles/theme';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: {
    lg: '50%',
    md: '65%',
    sm: '80%',
    xs: '95%',
  },
  maxWidth: '768px',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: `0px 10px 15px 0px ${colors.backdrop.shadow}`,
  p: 5,
};

const UploadModal = () => {
  const api = useAPI();
  const { backdoor } = useContext(AuthContext);
  const { uploadModal } = useContext(AssetTableContext);

  const {
    onDrop,
    onRemove,
    uploadSpreadsheet,
    uploadModalController,
    currentFile,
    uploadLoaded,
    spreadsheetUploadError,
  } = uploadModal;

  const downloadTemplate = useCallback(async () => {
    try {
      const res = await api.get('/assets/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'equipment_list_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading template:', err);
    }
  }, [api]);

  return (
    <Modal
      open={uploadModalController.open}
      onClose={uploadModalController.closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: 'flex',
        p: 1,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={style}>
        {/* Modal Title */}
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '28px',
            color: colors.black,
            mb: 3,
          }}
        >
          Excel Import
        </Typography>

        {!uploadLoaded && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: colors.dropzone.background,
              border: `1px solid ${colors.primary.light}`,
              color: colors.primary.dark,
            }}
          >
            Please wait while we process your request. For large files this could take a few
            minutes.
          </Alert>
        )}
        {spreadsheetUploadError && uploadLoaded && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: '#fdecea',
              border: `1px solid ${colors.error.main}`,
              color: colors.error.main,
            }}
          >
            {spreadsheetUploadError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Instructions Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                lineHeight: '20px',
                color: colors.black,
              }}
            >
              Download the{' '}
              <Link
                component="button"
                onClick={downloadTemplate}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: colors.secondary.main,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  verticalAlign: 'baseline',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Equipment List Template
                <DownloadIcon sx={{ fontSize: '12px', ml: 0.5, verticalAlign: 'middle' }} />
              </Link>{' '}
              and update your assets.
            </Typography>

            <Box
              component="ul"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                lineHeight: '20px',
                color: colors.black,
                m: 0,
                pl: '21px',
              }}
            >
              <li>Remove assets by deleting the entire row.</li>
              <li>Add assets by adding a new row and completing all required columns.</li>
              <li>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Columns must remain in the same order.
                </Box>
              </li>
              <li>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Columns G, K, and L should not be edited unless deleting the entire row.
                </Box>
              </li>
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              lineHeight: '20px',
              color: colors.black,
            }}
          >
            If your file has{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              more than 100 rows
            </Box>
            , please email it with your account number to personalproperty@utahcounty.gov in
            addition to uploading.
          </Typography>

          {/* Upload Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
            {currentFile ? (
              <FileUpload
                file={currentFile}
                onUpload={uploadSpreadsheet}
                onCancel={uploadModalController.closeModal}
                onRemove={onRemove}
                loaded={uploadLoaded}
              />
            ) : (
              <>
                <Upload onDrop={onDrop} loaded={uploadLoaded} disabled={backdoor} />
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Button
                    onClick={uploadModalController.closeModal}
                    sx={{
                      backgroundColor: colors.grey[100],
                      color: colors.grey[500],
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '20px',
                      textTransform: 'uppercase',
                      borderRadius: '4px',
                      height: '48px',
                      minWidth: '120px',
                      width: '192px',
                      '&:hover': {
                        backgroundColor: colors.grey[200],
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled
                    sx={{
                      backgroundColor: colors.secondary.light,
                      color: colors.white,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '20px',
                      textTransform: 'uppercase',
                      borderRadius: '4px',
                      height: '48px',
                      minWidth: '140px',
                      width: '192px',
                      '&:disabled': {
                        backgroundColor: colors.secondary.light,
                        color: colors.white,
                      },
                    }}
                  >
                    Import
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadModal;
